import clsx from 'clsx'
import { useEffect, useMemo } from 'react'

import type { LabeledOption } from '@fv/client-components/combobox'
import type { PropsWithClassName } from '@fv/client-types'
import type { Workflow } from '@fv/models'

import { SelectField } from '../inputs/SelectField'
import { ShipperAutocomplete } from '../inputs/ShipperAutocomplete'
import { type CarrierService, useCarrierServiceTypes } from './hooks'

type ServiceOption = LabeledOption<CarrierService>

type Props = {
  disabled?: boolean
  name: string
  onChange: (v: CarrierService | null) => void
  required?: boolean
  scac: string | undefined
  type: 'select' | 'typeahead'
  value: string | undefined
  workflow?: Workflow
}

export const CarrierServiceTypeahead = ({
  className,
  disabled,
  name,
  onChange,
  scac,
  required,
  type,
  value,
  workflow,
}: PropsWithClassName<Props>) => {
  const { isLoading, data: serviceTypes } = useCarrierServiceTypes(
    scac,
    workflow,
  )

  const options: ServiceOption[] = useMemo(
    () =>
      serviceTypes?.map(t => ({
        label: t.name,
        value: t,
      })) ?? [],
    [serviceTypes],
  )

  useEffect(() => {
    if (!value && serviceTypes?.[0]) {
      onChange(serviceTypes[0])
    }
  }, [serviceTypes, value, onChange])

  if (isLoading || type === 'typeahead') {
    return (
      <ShipperAutocomplete
        classNames={{
          container: clsx(className, 'w-full'),
        }}
        options={options}
        getOptionLabel={o => o.value.name}
        getOptionValue={o => o.value.id}
        onSelect={option => onChange(option?.value ?? null)}
        onValueChange={e => onChange(!e ? null : { id: e, name: e })}
        value={value ?? ''}
        name={name}
        isLoading={!disabled && isLoading}
        required={required}
        disabled={disabled}
      />
    )
  }

  if (type === 'select') {
    return (
      <SelectField
        name={name}
        disabled={disabled}
        className={className}
        required={required}
        value={value}
        options={options.map(o => ({ text: o.label, value: o.value.id }))}
        onChange={e =>
          onChange(serviceTypes?.find(t => t.id === e.target.value) ?? null)
        }
      />
    )
  }

  return null
}
